import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { formatLocal } from '../utils/timezones';

const WelcomeEmailTracker = ({ user, hoaData }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, sent, failed, pending, error
  const [copyingTemplate, setCopyingTemplate] = useState(null);
  
  // Get HOA timezone from settings - CRITICAL: Always use this for date displays
  const hoaTimezone = hoaData?.timezone || 'US/Central';

  useEffect(() => {
    loadUsers();
  }, [filter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Get all users with email delivery status
      const { data } = await api.get(`/api/board/users?hoa_id=${user.hoa_id}`);
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      bounced: 'bg-orange-100 text-orange-800',
      blocked: 'bg-red-100 text-red-800',
      spam_reported: 'bg-purple-100 text-purple-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusDisplay = (status) => {
    const displays = {
      sent: '📤 Sent (Not Verified)',
      delivered: '✅ Delivered',
      failed: '❌ Failed',
      pending: '⏳ Pending',
      error: '❌ Error',
      bounced: '⚠️ Bounced',
      blocked: '🚫 Blocked',
      spam_reported: '🚨 Spam Reported'
    };
    return displays[status] || status || '➖ No Email Sent';
  };

  const syncEmailStatuses = async () => {
    try {
      setSyncing(true);
      // Refresh user data - statuses are now updated via SendGrid webhooks
      await loadUsers();
      
      // Count current statuses from loaded users
      const delivered = users.filter(u => u.email_delivery_status === 'delivered').length;
      const blocked = users.filter(u => u.email_delivery_status === 'blocked').length;
      const bounced = users.filter(u => u.email_delivery_status === 'bounced').length;
      const spam = users.filter(u => u.email_delivery_status === 'spam_reported').length;
      const sent = users.filter(u => u.email_delivery_status === 'sent').length;
      
      alert(`✅ Statuses Refreshed!

📊 Current Status Summary:
• ✅ Delivered: ${delivered}
• 📤 Sent (awaiting confirmation): ${sent}
• 🚫 Blocked: ${blocked}
• ⚠️ Bounced: ${bounced}
• 🚨 Spam Reports: ${spam}

💡 Note: Statuses are automatically updated via SendGrid webhooks when email events occur.`);
    } catch (error) {
      console.error('Error refreshing statuses:', error);
      alert(`❌ Refresh failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const copyWelcomeTemplate = async (userId, userName) => {
    try {
      setCopyingTemplate(userId);
      const { data } = await api.get(`/api/board/user/${userId}/welcome-email-template`);
      
      // Try multiple copy methods
      let copySuccess = false;
      
      // Method 1: Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(data.html_body);
          copySuccess = true;
        } catch (e) {
          console.log('Clipboard API failed, trying fallback method');
        }
      }
      
      // Method 2: Fallback to textarea + execCommand
      if (!copySuccess) {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = data.html_body;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          const successful = document.execCommand('copy');
          textArea.remove();
          
          if (successful) {
            copySuccess = true;
          }
        } catch (e) {
          console.error('Fallback copy failed:', e);
        }
      }
      
      if (copySuccess) {
        // Show success with instructions
        alert(`✅ EMAIL TEMPLATE COPIED TO CLIPBOARD!

TO: ${data.credentials.email}
NAME: ${data.user_name}

CREDENTIALS IN EMAIL:
• Email: ${data.credentials.email}
• Password: ${data.credentials.temp_password}
• Login: ${data.credentials.login_url}

HOW TO SEND:
1. Open Gmail/Outlook
2. Create new email to: ${data.credentials.email}
3. Paste (Ctrl+V or Cmd+V) into email body
4. Send

✅ Template is already copied - just paste it!`);
      } else {
        // If both methods failed, show the content for manual copy
        const modalContent = `
COPY THIS TEMPLATE MANUALLY:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

TO: ${data.credentials.email}
SUBJECT: ${data.subject}

CREDENTIALS TO INCLUDE:
• Email: ${data.credentials.email}
• Temp Password: ${data.credentials.temp_password}
• Login URL: ${data.credentials.login_url}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

PLAIN TEXT VERSION:
${data.plain_text_body}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Select and copy the text above, then paste into your email.
        `;
        
        // Create a modal/dialog with selectable text
        const modal = document.createElement('div');
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        `;
        
        const content = document.createElement('div');
        content.style.cssText = `
          background: white;
          padding: 20px;
          border-radius: 8px;
          max-width: 90%;
          max-height: 80%;
          overflow: auto;
        `;
        
        const textarea = document.createElement('textarea');
        textarea.value = modalContent;
        textarea.style.cssText = `
          width: 100%;
          height: 400px;
          font-family: monospace;
          font-size: 12px;
          padding: 10px;
          border: 1px solid #ccc;
          border-radius: 4px;
        `;
        textarea.readOnly = true;
        
        const closeBtn = document.createElement('button');
        closeBtn.textContent = 'Close';
        closeBtn.style.cssText = `
          margin-top: 10px;
          padding: 8px 16px;
          background: #4f46e5;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        `;
        closeBtn.onclick = () => modal.remove();
        
        const selectBtn = document.createElement('button');
        selectBtn.textContent = 'Select All Text';
        selectBtn.style.cssText = `
          margin-top: 10px;
          margin-right: 10px;
          padding: 8px 16px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        `;
        selectBtn.onclick = () => {
          textarea.select();
          try {
            document.execCommand('copy');
            alert('✅ Text copied! You can close this window.');
          } catch (e) {
            alert('Please manually select and copy the text (Ctrl+C or Cmd+C)');
          }
        };
        
        content.appendChild(textarea);
        content.appendChild(selectBtn);
        content.appendChild(closeBtn);
        modal.appendChild(content);
        document.body.appendChild(modal);
        
        // Auto-select the text
        textarea.select();
      }
      
    } catch (error) {
      console.error('Error getting template:', error);
      alert(`❌ Failed to get template: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCopyingTemplate(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      // CRITICAL: Use HOA's configured timezone, not browser timezone
      return formatLocal(dateString, hoaTimezone, 'MMM d, yyyy h:mm a');
    } catch {
      return 'Invalid Date';
    }
  };

  const getFilteredUsers = () => {
    // Only show users who have email tracking (recently approved)
    // Users without email_delivery_status were approved before tracking was implemented
    const usersWithEmailTracking = users.filter(u => 
      u.email_delivery_status || 
      u.last_email_sent_at || 
      (u.email_delivery_attempts && u.email_delivery_attempts.length > 0)
    );
    
    if (filter === 'all') return usersWithEmailTracking;
    if (filter === 'pending') {
      // Pending = no status at all
      return usersWithEmailTracking.filter(u => !u.email_delivery_status);
    }
    if (filter === 'failed') {
      // Failed includes bounced, blocked, spam_reported, failed
      return usersWithEmailTracking.filter(u => 
        u.email_delivery_status === 'failed' || 
        u.email_delivery_status === 'bounced' || 
        u.email_delivery_status === 'blocked' ||
        u.email_delivery_status === 'spam_reported' ||
        u.email_delivery_status === 'error'
      );
    }
    return usersWithEmailTracking.filter(u => u.email_delivery_status === filter);
  };

  const filteredUsers = getFilteredUsers();

  // Get counts for filter badges - only count users with email tracking
  const usersWithTracking = users.filter(u => 
    u.email_delivery_status || 
    u.last_email_sent_at || 
    (u.email_delivery_attempts && u.email_delivery_attempts.length > 0)
  );
  
  const statusCounts = {
    all: usersWithTracking.length,
    sent: usersWithTracking.filter(u => u.email_delivery_status === 'sent').length,
    delivered: usersWithTracking.filter(u => u.email_delivery_status === 'delivered').length,
    failed: usersWithTracking.filter(u => u.email_delivery_status === 'failed' || u.email_delivery_status === 'bounced' || u.email_delivery_status === 'blocked' || u.email_delivery_status === 'spam_reported').length,
    error: usersWithTracking.filter(u => u.email_delivery_status === 'error').length,
    pending: usersWithTracking.filter(u => !u.email_delivery_status).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Welcome Email Tracker</h2>
          <p className="text-sm text-gray-600 mt-1">
            Track delivery status of welcome emails sent to approved homeowners
          </p>
        </div>
        <button
          onClick={syncEmailStatuses}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {syncing ? 'Refreshing...' : 'Refresh Statuses'}
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-1">About Welcome Email Tracker</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Statuses update automatically</strong> via SendGrid webhooks when delivery events occur</li>
          <li>• <strong>"Sent"</strong> = email sent, waiting for delivery confirmation</li>
          <li>• <strong>"Delivered"</strong> = SendGrid confirmed email reached recipient's mail server</li>
          <li>• <strong>"Blocked"</strong> = recipient's mail server rejected the email</li>
          <li>• <strong>Click "Copy Template"</strong> for blocked emails to send manually via your email client</li>
          <li>• <strong>Microsoft/Outlook often blocks:</strong> Use manual template for those recipients</li>
        </ul>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({statusCounts.all})
        </button>
        <button
          onClick={() => setFilter('delivered')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'delivered'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ✅ Delivered ({statusCounts.delivered})
        </button>
        <button
          onClick={() => setFilter('sent')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'sent'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📤 Sent ({statusCounts.sent})
        </button>
        <button
          onClick={() => setFilter('failed')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'failed'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ❌ Failed ({statusCounts.failed})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'pending'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ⏳ No Email ({statusCounts.pending})
        </button>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No users found with this status</p>
        </div>
      ) : (
        <>
          {/* Mobile scroll hint */}
          <p className="text-xs text-gray-500 mb-2 md:hidden">← Scroll horizontally to see all columns →</p>
          <div className="bg-white shadow overflow-x-auto rounded-lg">
            <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '800px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Homeowner
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Property
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Email Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Last Sent
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((homeowner) => (
                <tr key={homeowner.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {homeowner.first_name} {homeowner.last_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {homeowner.role}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{homeowner.email}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {homeowner.property_address || 'N/A'}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(homeowner.email_delivery_status)}`}>
                      {getStatusDisplay(homeowner.email_delivery_status)}
                    </span>
                    {homeowner.email_delivery_attempts && homeowner.email_delivery_attempts.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Attempts: {homeowner.email_delivery_attempts.length}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(homeowner.last_email_sent_at)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => copyWelcomeTemplate(homeowner.id, `${homeowner.first_name} ${homeowner.last_name}`)}
                      disabled={copyingTemplate === homeowner.id}
                      className="inline-flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed whitespace-nowrap"
                      title="Copy welcome email template to send manually"
                    >
                      {copyingTemplate === homeowner.id ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Copying...
                        </>
                      ) : (
                        <>
                          📋 Copy Template
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-900">{statusCounts.all}</div>
          <div className="text-sm text-gray-600">Total Users</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-800">{statusCounts.delivered}</div>
          <div className="text-sm text-green-600">Delivered</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-800">{statusCounts.sent}</div>
          <div className="text-sm text-blue-600">Sent (Unverified)</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-800">{statusCounts.failed}</div>
          <div className="text-sm text-red-600">Failed/Blocked</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-800">{statusCounts.pending}</div>
          <div className="text-sm text-yellow-600">No Email Sent</div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeEmailTracker;
